import React, { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import axios from 'axios';
import clsx from 'clsx';
import moment from 'moment';
import API from 'services/API';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPAvatar from 'UI/components/atoms/FPAvatar';
import { MapEntityInfo } from 'UI/components/molecules/MapEntityInfo';
import { MapEntityStatus } from 'UI/components/molecules/MapEntityStatus';
import { getInventoryDetailsLabel } from 'UI/components/molecules/MapInventoryItem/MapInventoryItem.utils';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { colors, CopyIcon } from 'UI/res';
import { nestTernary } from 'UI/utils';
import { getInitials } from 'UI/utils/getinitials';

import { useStyles } from './styles';

/**
 * @param {Object} props
 * @param {string} props.description
 * @param {string} [props.detailsLabel]
 * @param {string} props.entityType
 * @param {number} props.id
 * @param {string} props.initials
 * @param {number} props.markerType
 * @param {Function} props.onQuickViewClick
 * @param {Function} props.onCopyClick
 * @param {string} [props.recruiterEmail]
 * @param {string} [props.recruiterName]
 * @param {*} props.SearchProjectAction
 * @param {string} props.subtitle
 * @param {string} props.title
 * @param {string} props.type
 */
const InventoryPopover = ({
  id,
  recruiterName,
  type,
  title,
  subtitle,
  description,
  detailsLabel,
  recruiterEmail,
  entityType,
  markerType,
  initials,
  onQuickViewClick,
  onCopyClick,
  SearchProjectAction
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(null);

  const classes = useStyles();

  const initialsOrDefault = initials || getInitials(recruiterName);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setIsLoading(true);
        setLastActivity(null);
        const response = await API.get(
          `${Endpoints.Inventories}/getLastActivity?entityType=${entityType}&entityId=${id}`,
          { signal: controller.signal }
        );
        if (response?.data) {
          setLastActivity(response.data);
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        // eslint-disable-next-line no-console
        console.log(err);
      }
      setIsLoading(false);
    })();
    return () => controller.abort();
  }, [id, entityType]);

  const lastActivityContent = isLoading ? (
    <CircularProgress size={18} />
  ) : (
    nestTernary(
      !!lastActivity?.title,
      <span className={classes.itemDescription}>
        {lastActivity?.title} -{' '}
        {lastActivity?.date ? moment(lastActivity?.date).format(DateFormats.SimpleDate) : ''}
      </span>,
      <span className={classes.itemDescription}>No recently</span>
    )
  );

  return (
    <>
      <MapEntityInfo
        details={description}
        detailsLabel={detailsLabel || getInventoryDetailsLabel({ entity: entityType })}
        entityStatus={<MapEntityStatus markerType={markerType} type={type} entity={entityType} />}
        entitySubtitle={subtitle}
        entityTitle={title}
        lastActivityContent={lastActivityContent}
      />
      <Divider className={classes.divider} />
      <div className={classes.recruiterInfo}>
        <FPAvatar>{initialsOrDefault}</FPAvatar>
        <div className={classes.recruiterName}>
          <div>Recruiter: {recruiterName}</div>
          <div className={classes.emailContainer}>
            <Typography className={classes.emailText} onClick={() => onCopyClick(recruiterEmail)}>
              {recruiterEmail}
            </Typography>
            <CopyIcon
              fill={colors.navLink}
              size={14}
              className={clsx(classes.copyButton, 'copyButton')}
              title="Copy to Clipboard"
              onClick={() => onCopyClick(recruiterEmail)}
            />
          </div>
        </div>
      </div>
      <div className={classes.linkContainer}>
        <SearchProjectAction text="Add to Search Project" />
        <FPActionButton
          text="View Details"
          size="small"
          className={classes.previewButton}
          id={id}
          onClick={onQuickViewClick}
        />
      </div>
    </>
  );
};

InventoryPopover.defaultProps = {
  type: '',
  recruiterName: '',
  recruiterEmail: '',
  description: '',
  detailsLabel: '',
  entityType: '',
  id: null,
  initials: '',
  subtitle: '',
  title: ''
};

export default InventoryPopover;
